import { IReportMetadata } from '@app/shared/report/report-data.service';
import { IDashboardSort } from '../dashboard/dashboard-metadata.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { Inject, Injectable } from '@angular/core';
import { AgAxisLabelFormatterParams, AgCartesianSeriesTooltipRendererParams, AgChartThemeOverrides, AgLineSeriesTooltipRendererParams } from 'ag-grid-enterprise';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community/dist/types/src/options/chart/legendOptions';
import { ChartType, CreatePivotChartParams, CreateRangeChartParams, FirstDataRenderedEvent, GridApi, IFilterComp, RowDataUpdatedEvent } from 'ag-grid-community';
import { DOCUMENT } from '@angular/common';
import { IExtendedDocument } from '@type_definitions/datatypes';
import { IEmbeddedReportGridReady } from '@app/shared/report/embedded-report/embedded-report.component';

export interface IChartReport {
  id?:string;
  title?:string;
  metadata?:IReportMetadata;
  sort?:IDashboardSort[];
  collapsed?:boolean;
  params?:any;
  listFaqs?:string[];
}

export interface IChartGridContext {
  chartCreationType:ChartCreationType;
}

export const SECTION_RESOURCES:string = 'RESOURCES';
export const SECTION_DATE_REVENUE:string = 'DATE_REVENUE';
export const SECTION_MOM_REVENUE:string = 'MOM_REVENUE';
export const SECTION_COUNTRY_REVENUE:string = 'COUNTRY_REVENUE';
export const SECTION_CUSTOMER_REVENUE:string = 'CUSTOMER_REVENUE';
export const SECTION_CATEGORY_COUNT:string = 'CATEGORY_COUNT';
export const ROW_GROUP_COLUMN_ID:string = 'ag-Grid-AutoColumn';
const CHART_TYPE_GROUPED_COLUMN:ChartType = 'groupedColumn';

export enum ChartCreationType {
  PIVOT = 'pivot',
  RANGE = 'range'
}

@Injectable()
export class ChartService {
  constructor(
    private formattingService:FormattingService,
    @Inject(DOCUMENT) private document:IExtendedDocument
  ) {
    /**/
  }
  
  public chartInit(event:IEmbeddedReportGridReady):void {
    switch (event.sectionId) {
      case SECTION_DATE_REVENUE:
        event.gridApi.setGridOption('onFirstDataRendered', (event:FirstDataRenderedEvent):void => {
          this.createGroupedColumnChart(event.api, `${SECTION_DATE_REVENUE}_CHART`);
        });
        break;
        
      case SECTION_MOM_REVENUE:
        // set pivot row totals to undefined because we don't want ag-grid to add row totals at the end of the columns
        event.gridApi.setGridOption('pivotRowTotals', undefined);
        event.gridApi.setGridOption('onFirstDataRendered', (event:FirstDataRenderedEvent):void => {
          // create pivot chart
          this.createPivotChart(event.api, `${SECTION_MOM_REVENUE}_CHART`);
          
          // sort row group column (in this case, it's month column)
          event.api.applyColumnState({
            state: [{ colId: ROW_GROUP_COLUMN_ID, sort: 'asc' }],
            defaultState: { sort: null }
          });
        });
        break;
        
      case SECTION_COUNTRY_REVENUE:
        event.gridApi.setGridOption('onFirstDataRendered', (event:FirstDataRenderedEvent):void => {
          this.createGroupedColumnChart(event.api, `${SECTION_COUNTRY_REVENUE}_CHART`);
        });
        break;
        
      case SECTION_CUSTOMER_REVENUE:
        // whenever data is changed, we apply TOP-10 filter
        event.gridApi.setGridOption('onRowDataUpdated', (event:RowDataUpdatedEvent):void => {
          // filter or TOP-10
          const top10Customers:string[] = [];
          const gridApi:GridApi = event.api;
          gridApi.forEachNodeAfterFilterAndSort(((rowNode) => {
            if (rowNode.groupData) {
              if (top10Customers.length <= 10) {
                top10Customers.push(rowNode.key);
              }
            }

          }));

          // Set the model for the customer filter
          const filterInstance:IFilterComp = gridApi.getColumnFilterModel('Customer');

          if (filterInstance) {
            filterInstance.setModel({
              type: 'set',
              values: top10Customers
            });
          }

          gridApi.onFilterChanged();
        });

        event.gridApi.setGridOption('onFirstDataRendered', (event:FirstDataRenderedEvent):void => {
          this.createGroupedColumnChart(event.api, `${SECTION_CUSTOMER_REVENUE}_CHART`);
        });
        break;
        
      case SECTION_CATEGORY_COUNT:
        event.gridApi.setGridOption('onFirstDataRendered', (event:FirstDataRenderedEvent):void => {
          this.createPivotChart(event.api, `${SECTION_CATEGORY_COUNT}_CHART`);
        });
        break;
    }
  }

  public createGroupedColumnChart(gridApi:GridApi, chartElementId:string, currency?:string):void {
    const createRangeChartParams:CreateRangeChartParams = {
      cellRange: {
        rowStartIndex: 0,
        rowEndIndex: null,
        columns: [
          ROW_GROUP_COLUMN_ID,
          'Total_price'
        ]
      },
      chartType: CHART_TYPE_GROUPED_COLUMN,
      chartContainer: this.document.querySelector(`#${chartElementId}`),
      suppressChartRanges: true,
      aggFunc: 'sum',
      chartThemeOverrides: this.createChartOptionsProcessor(chartElementId, currency)
    };

    // this is to add context for enlarge chart button
    gridApi.setGridOption('context', <IChartGridContext>{ chartCreationType: ChartCreationType.RANGE });
    gridApi.createRangeChart(createRangeChartParams);
  }

  public createPivotChart(gridApi:GridApi, chartElementId:string):void {
    const createPivotChartParams:CreatePivotChartParams = {
      chartType: CHART_TYPE_GROUPED_COLUMN,
      chartContainer: this.document.querySelector(`#${chartElementId}`),
      chartThemeOverrides: this.createChartOptionsProcessor(chartElementId)
    };

    // this is to add context for enlarge chart button
    gridApi.setGridOption('context', <IChartGridContext>{ chartCreationType: ChartCreationType.PIVOT });
    gridApi.createPivotChart(createPivotChartParams);
  }

  public getDateRangeValue(param:any):Date {
    return param && param.match(/\d{2}\/\d{2}\/\d{4}/)? this.formattingService.parseDate(param) : param;
  }

  // Creates a chart customizer:
  // - formats decimal numbers in Y-axis and legend (adds currency if applicable)
  // - shortens strings in X-access
  private createChartOptionsProcessor(chartElementId:string, currency?:string):AgChartThemeOverrides {
    return {
      common: {
        legend: {
          enabled: true,
          position: 'right',
          item: {
            label: {
              formatter: (params:AgChartLegendLabelFormatterParams) => {
                // remove Spend from legend for yearly revenue chart (e.g. '2025 - Spend' to '2025')
                if (chartElementId.includes(SECTION_MOM_REVENUE)) {
                  return params.value?.replace(/\s-\sSpend/ig, '');
                }

                return params.value;
              }
            }
          }
        },
        axes: {
          number: {
            label: {
              formatter: (params:AgAxisLabelFormatterParams):string => {
                return this.formattingService.formatNumber(params.value);
              }
            }
          },
          'grouped-category': {
            label: {
              formatter: this.categoryLabelFormatter
            }
          },
          category: {
            label: {
              formatter: this.categoryLabelFormatter
            }
          }
        }
      },
      line: {
        series: {
          tooltip: {
            renderer: (params:AgLineSeriesTooltipRendererParams) => this.tooltipRenderer(params, currency)
          }
        }
      }
    };
  }

  private tooltipRenderer(params:AgCartesianSeriesTooltipRendererParams, currency?: string):string {
    const label:string = params.datum[ params.xKey ].labels? `${params.datum[ params.xKey ].labels[ 0 ]}: ` : '';
    let value:string;
    if (currency) {
      value = this.formattingService.formatAmount(params.datum[ params.yKey ], currency);
    } else {
      value = params.datum[ params.yKey ];
    }
    return `<div class="ag-chart-tooltip-title" style="color: white; background-color: ${params.color}">${params.yName}</div>
      <div class="ag-chart-tooltip-content">${label}${value}</div>`;
  }

  private categoryLabelFormatter = (params:AgAxisLabelFormatterParams):string => {
    if (params.value && params.value.length > 13) {
      return `${params.value.substring(0, 9)}...`;
    }
    return params.value;
  };
}